import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import { Suspense, useEffect, useState, type ReactNode } from "react";
import { KeepAppRailOnMobile } from "@/components/sidebar/mobileRailVisibility";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { deferNavigationSettle } from "@/hooks/useNavigationTimingReporting";
import { codesmithSurface, useCodesmithSurface } from "@/lib/codesmithSurface";
import { ChatExperimentsExtensionBridge } from "@/components/agent-session/useChatExperimentsExtensionBridge";
import { getCachedCodesmithEnabled, getCachedLastActiveOrg } from "@/components/sidebar/useCodesmithEnabledCache";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { useAuth } from "@/components/AuthProvider";
import { SessionSkeleton } from "./SessionSkeleton";
import { CodesmithNeedsOrganization } from "./CodesmithNeedsOrganization";
import { CodesmithDisabledNotice } from "./CodesmithDisabledNotice";
import { deepLinkedSessionId } from "./usePrefetchDeepLinkedSession";
import { namedOrgKey } from "@/lib/named-org";

// Codesmith renders the tab title once mounted (the session's title when it
// has one), so exactly one <title> exists at a time: this one stands in
// until then.
function ShellFallback() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><title>[code]smith</title><SessionSkeleton /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// Codesmith owns browser-only draft persistence and realtime subscriptions,
// so keep its render boundary client-only.
const Codesmith = dynamic(() => import("./Codesmith"), {
  ssr: false,
  loading: () => <ShellFallback />
});
function isSessionsPath(pathname: string): boolean {
  return /^(\/\[org\])?\/sessions(\/|$)/.test(pathname);
}

// A deep-linked session renders while the org list loads: every Codesmith
// route carries its org in the URL, and reading a session needs no
// installation. Drafts and subpages create installation-scoped work, so a
// shell opened on one waits for orgs.
function ShellContent(t0) {
  const $ = _c(28);
  const {
    children
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] !== router.pathname || $[1] !== router.query.sessionPath) {
    t1 = () => deepLinkedSessionId(router.pathname, router.query.sessionPath) !== null;
    $[0] = router.pathname;
    $[1] = router.query.sessionPath;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [openedOnSession] = useState(t1);
  const {
    codesmithEnabled,
    codesmithNeedsOrganization,
    orgsResolved,
    orgsError,
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const {
    user
  } = useAuth();
  const urlInstanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  const surface = useCodesmithSurface(router.query.embed);
  let t2;
  if ($[3] !== router.pathname) {
    t2 = isSessionsPath(router.pathname);
    $[3] = router.pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isSubpage = !t2;
  const codesmithDisabled = orgsResolved && !codesmithEnabled;
  let t3;
  let t4;
  if ($[5] !== codesmithDisabled || $[6] !== codesmithNeedsOrganization || $[7] !== router) {
    t3 = () => {
      if (!router.isReady) {
        return;
      }
      if (!codesmithDisabled || codesmithNeedsOrganization) {
        return;
      }
      if (codesmithSurface(router.query.embed) !== "web") {
        return;
      }
      const org = router.query.org;
      if (typeof org !== "string" || !org) {
        return;
      }
      router.replace(`/${org}/runs/jobs`);
    };
    t4 = [router, router.isReady, router.query.org, router.query.embed, codesmithDisabled, codesmithNeedsOrganization];
    $[5] = codesmithDisabled;
    $[6] = codesmithNeedsOrganization;
    $[7] = router;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  if (orgsError && !orgsResolved) {
    throw orgsError;
  }
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const lastSeenWithoutCodesmith = getCachedCodesmithEnabled(activeNamedOrg ?? (urlOrg ? {
    orgName: urlOrg,
    instanceSlug: urlInstanceSlug
  } : getCachedLastActiveOrg(urlInstanceSlug))) === false;
  const waitsForOrgs = !openedOnSession || lastSeenWithoutCodesmith;
  if (!orgsResolved && waitsForOrgs) {
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <ShellFallback />;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  if (codesmithDisabled && codesmithNeedsOrganization) {
    let t5;
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <title>[code]smith</title>;
      t6 = <KeepAppRailOnMobile />;
      $[11] = t5;
      $[12] = t6;
    } else {
      t5 = $[11];
      t6 = $[12];
    }
    const t7 = activeNamedOrg?.instanceSlug ?? null;
    let t8;
    if ($[13] !== t7) {
      t8 = <>{t5}{t6}<CodesmithNeedsOrganization instanceSlug={t7} /></>;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  if (codesmithDisabled) {
    let t5;
    let t6;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <title>[code]smith</title>;
      t6 = <KeepAppRailOnMobile />;
      $[15] = t5;
      $[16] = t6;
    } else {
      t5 = $[15];
      t6 = $[16];
    }
    let t7;
    if ($[17] !== surface) {
      t7 = surface !== "web" && <CodesmithDisabledNotice />;
      $[17] = surface;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t7) {
      t8 = <>{t5}{t6}{t7}</>;
      $[19] = t7;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  let t5;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChatExperimentsExtensionBridge />;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== activeNamedOrg) {
    t6 = namedOrgKey(activeNamedOrg);
    $[22] = activeNamedOrg;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== children || $[25] !== isSubpage || $[26] !== t6) {
    t7 = <>{t5}<Codesmith key={t6} isSubpage={isSubpage}>{children}</Codesmith></>;
    $[24] = children;
    $[25] = isSubpage;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
}

/**
 * The Codesmith app shell (session sidebar + main pane), mounted once by the
 * `(codesmith)` route group layout so navigating between the sessions,
 * automations, and MCP connections routes swaps only the main pane. On the
 * sessions route Codesmith owns the main pane itself; every other route
 * renders its page as `children` inside the shell.
 */
export function CodesmithShell(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  deferNavigationSettle();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ShellFallback />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Suspense fallback={t1}><ShellContent>{children}</ShellContent></Suspense>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}